import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Minus, Plus, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { useSite } from "../context/SiteContext";
import { MaskedLines, FadeUp, ParallaxImage } from "../components/motion";
import SEO from "../components/SEO";
import { IMAGES } from "../data/products";

const FaqItem = ({ item, isOpen, onToggle, index }) => (
  <div className="border-b border-ink/10" data-testid={`faq-item-${index}`}>
    <button
      onClick={onToggle}
      aria-expanded={isOpen}
      data-testid={`faq-question-${index}`}
      className="flex w-full items-center justify-between gap-6 py-7 text-left"
    >
      <span className="font-display text-xl font-bold lg:text-2xl">{item.q}</span>
      <span className={`shrink-0 border p-2 transition-colors duration-300 ${isOpen ? "border-rouge bg-rouge text-cream" : "border-ink/20"}`}>
        {isOpen ? <Minus size={16} /> : <Plus size={16} />}
      </span>
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          className="overflow-hidden"
        >
          <p className="max-w-2xl pb-8 text-sm leading-relaxed text-stone2" data-testid={`faq-answer-${index}`}>{item.a}</p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const HowItWorks = () => {
  const { t } = useLanguage();
  const { img } = useSite();
  const [open, setOpen] = useState(0);

  return (
    <>
      <SEO page="how" />
      <section className="mx-auto max-w-[1400px] px-6 pt-40 lg:px-12 lg:pt-48" data-testid="how-header">
        <div className="grid items-end gap-16 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <p className="overline-label text-rouge">{t.how.overline}</p>
            <h1 className="mt-8 font-display text-5xl font-bold leading-[1.02] tracking-tighter sm:text-6xl lg:text-7xl">
              <MaskedLines
                inView
                lines={[
                  <span>{t.how.title}</span>,
                  <span className="italic font-medium text-rouge">{t.how.titleItalic}</span>,
                ]}
              />
            </h1>
            <FadeUp delay={0.3}>
              <p className="mt-8 max-w-xl text-base leading-relaxed text-stone2 md:text-lg">{t.how.intro}</p>
            </FadeUp>
          </div>
          <FadeUp delay={0.2} className="lg:col-span-5">
            <ParallaxImage
              src={img("howHero", IMAGES.howHero)}
              alt="Press on nagels resultaat — On It Nails Herzele"
              className="aspect-[4/3]"
              speed={0.1}
            />
          </FadeUp>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 py-24 lg:px-12 lg:py-32" data-testid="how-options">
        <FadeUp>
          <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t.how.optionsTitle}</h2>
        </FadeUp>
        <div className="mt-12 grid gap-px border border-ink/10 bg-ink/10 md:grid-cols-2">
          {[
            { nr: "01", title: t.how.option1Title, text: t.how.option1Text, cta: t.how.option1Cta, to: "https://www.onitnails.com/onitnailscollectie/p/sizing-kit", external: true, testid: "option-sizing-kit" },
            { nr: "02", title: t.how.option2Title, text: t.how.option2Text, cta: t.how.option2Cta, to: "/collectie", external: false, testid: "option-self-measure" },
          ].map((opt) => (
            <FadeUp key={opt.nr} className="flex flex-col bg-cream p-10 lg:p-14">
              <span className="font-display text-6xl font-bold text-blush" aria-hidden="true">{opt.nr}</span>
              <h3 className="mt-8 font-display text-2xl font-bold lg:text-3xl">{opt.title}</h3>
              <p className="mt-5 flex-1 text-sm leading-relaxed text-stone2">{opt.text}</p>
              {opt.external ? (
                <a href={opt.to} target="_blank" rel="noreferrer" data-testid={opt.testid} className="group mt-10 inline-flex w-fit items-center gap-3 bg-ink px-7 py-3.5 text-xs font-bold uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-rouge">
                  {opt.cta} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </a>
              ) : (
                <Link to={opt.to} data-testid={opt.testid} className="group mt-10 inline-flex w-fit items-center gap-3 border border-ink px-7 py-3.5 text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-ink hover:text-cream">
                  {opt.cta} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              )}
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="bg-ink py-24 text-cream lg:py-32" data-testid="how-steps">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <FadeUp>
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t.how.stepsTitle}</h2>
          </FadeUp>
          <div className="mt-16 grid gap-16 lg:grid-cols-2">
            {t.how.steps.map((step, i) => (
              <FadeUp key={step.nr} delay={i * 0.15} className="border-t border-cream/15 pt-10" data-testid={`how-step-${i + 1}`}>
                <p className="overline-label text-blush">{step.nr}</p>
                <h3 className="mt-4 font-display text-2xl font-bold lg:text-3xl">{step.title}</h3>
                <p className="mt-5 max-w-md text-sm leading-relaxed text-cream/65">{step.text}</p>
                <div className="img-frame mt-8 aspect-[16/10] max-w-md">
                  <img
                    src={i === 0 ? IMAGES.measure1 : IMAGES.measure2}
                    alt={i === 0 ? "Nagels opmeten met meetlint of plakband — handleiding" : "Maattabel press on nagels 00 tot 9"}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.2}>
            <p className="mt-16 inline-flex items-start gap-3 border border-cream/15 bg-cream/5 p-6 text-sm text-cream/75">
              <Sparkles size={18} className="mt-0.5 shrink-0 text-blush" />
              {t.how.sizeNote}
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 py-24 lg:px-12 lg:py-32" data-testid="how-tips">
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <FadeUp>
              <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t.how.tipsTitle}</h2>
              <ul className="mt-10 space-y-6">
                {t.how.tips.map((tip, i) => (
                  <li key={i} className="flex gap-5 border-b border-ink/10 pb-6" data-testid={`tip-${i}`}>
                    <span className="font-display text-2xl font-bold text-rouge" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
                    <p className="text-sm leading-relaxed text-stone2">{tip}</p>
                  </li>
                ))}
              </ul>
            </FadeUp>
          </div>
          <div className="lg:col-span-7">
            <FadeUp delay={0.15}>
              <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t.how.faqTitle}</h2>
              <div className="mt-10 border-t border-ink/10" data-testid="faq-list">
                {t.how.faq.map((item, i) => (
                  <FaqItem key={i} index={i} item={item} isOpen={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
                ))}
              </div>
            </FadeUp>
          </div>
        </div>
      </section>
    </>
  );
};

export default HowItWorks;
